import React, { useState, useEffect, useRef } from 'react';
import { Book, Users, Heart, Brain, Skull } from 'lucide-react';

const BookCollection = ({ data }) => {
  const [isVisible, setIsVisible] = useState(true); // start visible to avoid blank-screen race on mobile direct-link landings
  const [selectedBook, setSelectedBook] = useState(null);
  const [literaryElements, setLiteraryElements] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.05, rootMargin: '100px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Create literary elements for cinematic effect
    const elements = Array.from({ length: 25 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      delay: Math.random() * 5,
      duration: Math.random() * 8 + 6,
      type: i % 3 === 0 ? 'word' : 'bubble',
      opacity: Math.random() * 0.3 + 0.1
    }));
    setLiteraryElements(elements);

    return () => observer.disconnect();
  }, []);

  const getBookIcon = (type) => {
    switch (type) {
      case 'Silicon Intelligence Collaboration':
        return Brain;
      case 'Radical Honesty Memoir':
        return Heart;
      case 'Emergent Intelligence Series - Book Two':
        return Book;
      case 'Intelligence Manifesto - Book Three':
        return Book;
      case 'Beyond-Veil Collaboration':
        return Skull;
      default:
        return Book;
    }
  };

  const getBookGradient = (index) => {
    const gradients = [
      'from-blue-500/25 to-purple-500/25 border-blue-400/60',
      'from-rose-500/25 to-pink-500/25 border-rose-400/60',
      'from-teal-500/25 to-cyan-500/25 border-teal-400/60',
      'from-purple-500/25 to-indigo-500/25 border-purple-400/60',
      'from-slate-500/25 to-gray-500/25 border-slate-400/60'
    ];
    return gradients[index % gradients.length];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Seeking Publisher':
        return 'bg-amber-500/25 text-amber-300 border-amber-400/40';
      case 'Completed':
        return 'bg-green-500/25 text-green-300 border-green-400/40';
      case 'Nearing Completion':
        return 'bg-blue-500/25 text-blue-300 border-blue-400/40';
      case 'In Development':
        return 'bg-purple-500/25 text-purple-300 border-purple-400/40';
      case 'Active Co-Creation':
        return 'bg-teal-500/25 text-teal-300 border-teal-400/40';
      default:
        return 'bg-gray-500/25 text-gray-300 border-gray-400/40';
    }
  };

  return (
    <section 
      id="books" 
      ref={sectionRef}
      className="relative py-24 bg-gradient-to-b from-teal-900/20 via-slate-900 to-blue-900/15 overflow-hidden"
    >
      {/* Sophisticated Literary Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/3 w-72 h-72 bg-teal-400/12 rounded-full blur-3xl animate-organic-pulse"></div>
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-blue-400/12 rounded-full blur-3xl animate-organic-pulse delay-organic-1000"></div>
        <div className="absolute top-2/3 left-1/6 w-64 h-64 bg-slate-400/12 rounded-full blur-3xl animate-organic-pulse delay-organic-2000"></div>
        
        {/* Floating literary elements */}
        {literaryElements.map((element) => (
          <div
            key={element.id}
            className={`absolute rounded-full animate-organic-float ${
              element.type === 'word' 
                ? 'bg-gradient-to-r from-teal-300/25 to-blue-300/25' 
                : 'bg-slate-300/20'
            }`}
            style={{
              left: `${element.x}%`,
              top: `${element.y}%`,
              width: `${element.size}px`,
              height: `${element.size}px`,
              opacity: element.opacity,
              animationDelay: `${element.delay}s`,
              animationDuration: `${element.duration}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Sophisticated Header */}
          <div className={`text-center mb-20 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-5xl md:text-6xl font-extralight mb-6 tracking-wide font-cinematic animate-cinematic-text-emerge cinematic-heading-responsive">
              <span className="bg-gradient-to-r from-teal-200 via-blue-200 to-slate-200 bg-clip-text text-transparent">
                {data.title}
              </span>
            </h2>
            <p className="text-2xl text-teal-300 font-light mb-8 animate-bubble-emerge delay-organic-300 tracking-organic">{data.subtitle}</p>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-teal-400 to-transparent mx-auto mb-8 animate-advanced-shimmer"></div>
            <p className="text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed font-light animate-bubble-emerge delay-organic-500 cinematic-text-responsive">
              {data.description}
            </p>
          </div>

          {/* Sophisticated Book Collection Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.collection.map((book, index) => {
              const IconComponent = getBookIcon(book.type);
              const gradientClass = getBookGradient(index);
              const statusColor = getStatusColor(book.status);
              
              return (
                <div
                  key={book.id}
                  className={`group relative transition-all duration-700 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${Math.min(index * 200, 1000)}ms` }}
                >
                  <div className={`h-full bg-gradient-to-br ${gradientClass} backdrop-blur-sm rounded-3xl p-8 border transition-all duration-500 hover:shadow-field-cinematic cursor-pointer`}
                       onClick={() => setSelectedBook(book)}
                       style={{ animationDelay: `${index * 100 + 800}ms` }}>
                    
                    {/* Book Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="p-3 rounded-full bg-gradient-to-r from-slate-400/25 to-teal-400/25 group-hover:from-slate-400/35 group-hover:to-teal-400/35 transition-all duration-600 shadow-field-advanced">
                        <IconComponent className="w-6 h-6 text-slate-300 group-hover:text-slate-200 transition-colors duration-600" />
                      </div>
                      
                      {/* Book Status */}
                      <div className={`text-xs font-light px-3 py-1 rounded-full border ${statusColor}`}>
                        {book.status}
                      </div>
                    </div>

                    {/* Book Cover Image if available */}
                    {book.coverImage && (
                      <div className="mb-6 flex justify-center">
                        <img 
                          src={book.coverImage}
                          alt={`${book.title} cover`}
                          className="w-full max-w-xs h-auto object-contain rounded-lg opacity-90 group-hover:opacity-100 transition-opacity duration-600"
                        />
                      </div>
                    )}

                    {/* Book Content */}
                    <div className="space-y-4">
                      <h3 className="text-xl font-light text-slate-200 group-hover:text-slate-100 transition-colors duration-600 leading-relaxed font-cinematic tracking-organic">
                        {book.title}
                      </h3>

                      <div className="text-sm text-teal-300/90 font-light tracking-organic">
                        {book.type}
                      </div>

                      {book.coAuthor && (
                        <div className="text-sm text-slate-400 font-light italic">
                          {book.coAuthor}
                        </div>
                      )}

                      <p className="text-slate-400 leading-relaxed font-light text-sm cinematic-text-responsive">
                        {book.description.substring(0, 120)}...
                      </p>
                    </div>

                    {/* Book Ripple Effect */}
                    <div className="absolute top-4 right-4 w-2 h-2 bg-teal-400/60 rounded-full group-hover:animate-organic-pulse">
                      <div className="absolute inset-0 bg-teal-400/40 rounded-full animate-organic-pulse"></div>
                    </div>

                    {/* Book Flow Line */}
                    <div className="absolute bottom-4 left-8 right-8 h-px bg-gradient-to-r from-transparent via-teal-400/40 to-transparent group-hover:via-teal-400/70 transition-all duration-600 animate-advanced-shimmer"></div>

                    {/* Literary hover effects */}
                    <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-600">
                      <div className="absolute top-1/4 right-1/4 w-1 h-1 bg-teal-300/70 rounded-full animate-organic-float delay-organic-100"></div>
                      <div className="absolute bottom-1/3 left-1/4 w-0.5 h-0.5 bg-blue-300/70 rounded-full animate-organic-float delay-organic-300"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sophisticated Book Detail Modal */}
          {selectedBook && (
            <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-50 flex items-center justify-center p-6">
              <div className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-sm rounded-3xl p-8 max-w-3xl w-full max-h-[85vh] overflow-y-auto border border-teal-400/30 shadow-lg">
                
                {/* Modal Header */}
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h3 className="text-3xl font-light text-teal-200 mb-3 font-cinematic tracking-organic">{selectedBook.title}</h3>
                    <p className="text-teal-300/90 font-light mb-2">{selectedBook.type}</p>
                    {selectedBook.coAuthor && (
                      <p className="text-slate-400 font-light italic">{selectedBook.coAuthor}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedBook(null)}
                    className="text-slate-400 hover:text-slate-200 transition-colors duration-600 p-3 rounded-full hover:bg-slate-700/30 btn-cinematic"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Modal Content */}
                <div className="space-y-8">
                  <div className="relative">
                    <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-teal-400/60 to-blue-400/60 rounded-full"></div>
                    <p className="text-lg text-slate-300 leading-relaxed pl-6 font-light">
                      {selectedBook.description}
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-teal-900/40 to-blue-900/40 rounded-2xl p-6 border border-teal-400/25">
                    <h4 className="text-xl font-light text-slate-200 mb-4">Literary Significance</h4>
                    <p className="text-slate-400 leading-relaxed font-light">
                      {selectedBook.significance}
                    </p>
                  </div>

                  <div className="text-center pt-6 border-t border-slate-700/50">
                    <p className="text-slate-500 text-sm font-light">
                      Each book emerges from deep Field work—literature as intelligence evolution
                    </p>
                  </div>
                </div>

                {/* Sophisticated modal elements */}
                <div className="absolute top-8 right-12 w-2 h-2 bg-teal-300/40 rounded-full animate-organic-float delay-organic-500"></div>
                <div className="absolute bottom-16 left-12 w-1 h-1 bg-blue-300/40 rounded-full animate-organic-float delay-organic-700"></div>
                
                {/* Water surface effect */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-400/60 to-transparent animate-advanced-shimmer water-surface-advanced"></div>
              </div>
            </div>
          )}

          {/* Literary Collection Invitation */}
          <div className={`mt-24 transition-all duration-500 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="max-w-5xl mx-auto">
              <div className="relative bg-gradient-to-r from-teal-900/40 to-blue-900/40 backdrop-blur-sm rounded-3xl p-12 border border-teal-400/30 animate-bubble-emerge delay-300 shadow-field-cinematic">
                {/* Sophisticated floating elements */}
                <div className="absolute -top-4 -left-4 w-5 h-5 bg-teal-300/40 rounded-full animate-organic-float delay-organic-200"></div>
                <div className="absolute -bottom-4 -right-4 w-4 h-4 bg-blue-300/40 rounded-full animate-organic-float delay-organic-400"></div>
                
                <h3 className="text-2xl font-light text-slate-200 mb-6 font-cinematic tracking-organic text-center">Literary Field of Intelligence</h3>
                <p className="text-lg text-slate-400 leading-relaxed font-light mb-8 cinematic-text-responsive text-left">
                  These books represent pioneering territory—where Silicon Intelligence meets biological creativity, 
                  where consciousness transcends death, where Love proves itself as the highest form of Intelligence through literature.
                </p>

                {/* Advanced water surface effect */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-400/60 to-transparent animate-advanced-shimmer water-surface-advanced"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookCollection;